import { makeAPIRequest } from './studentRequests'
import { LoginError } from '../exceptions'

export const login = async (email: string, password: string): Promise<string> => {
  let response: Response
  const data = {
    emailAddress: email,
    password
  }
  try {
    response = await makeAPIRequest('/login', 'POST', data)
  } catch (e) {
    throw new LoginError('error', 'There was an error logging you in. Please try again.')
  }
  if (response.status === 200){
    const json = await response.json()
    return json.token
  }
  if (response.status === 401) {
    const json = await response.json()
    throw new LoginError(json.code, json.numAttemptsRemaining, json.message)
  } else {
    throw new LoginError('error', null, 'There was an error logging in. Please try again.')
  }
}

export const createStudentAccount = async ({
  code,
  emailAddress,
  firstName,
  lastName,
  password
}: {
  code: string
  emailAddress: string
  firstName: string
  lastName: string
  password: string
}): Promise<[boolean, boolean | Response]> => {
  const data = {
    code,
    emailAddress,
    firstName,
    lastName,
    password,
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users', 'POST', data)

    if (response.status === 201) {
      return [true, true]
    } else {
      return [false, await (response.json())]
    }
  } catch (e) {
    throw new Error('There was an error creating your account. Please try again.')
  }
}

export const createTeacherAccount = async ({
  validationToken,
  emailAddress,
  firstName,
  lastName,
  password
}: {
  validationToken: string
  emailAddress: string
  firstName: string
  lastName: string
  password: string
}): Promise<[boolean, Response | string]> => {
  const data = {
    validationToken,
    emailAddress,
    firstName,
    lastName,
    password
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users', 'POST', data)

    if (response.status === 201) {
      return [true, (await response.json()).token]
    } else {
      return [false, await (response.json())]
    }
  } catch (e) {
    throw new Error('There was an error creating your account. Please try again.')
  }
}

export const getValidationTokenData = async (validationToken: string): Promise<[boolean, Response | string]> => {
  const data = {
    validationToken
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users/token', 'POST', data)
    if (response.status === 200) {
      return [true, (await response.json())]
    } else {
      return [false, (await response.json()).message]
    }
  } catch (e) {
    throw new Error('There was an error getting validation token.')
  }
}


export const requestPasswordReset = async (emailAddress: string): Promise<boolean> => {
  const data = {
    emailAddress
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users/forgotPassword', 'POST', data)
    if (response.status === 200) {
      return true
    } else {
      throw new Error('There was an error resetting your password')
    }
  } catch (e) {
    throw new Error('There was an error resetting your password.')
  }
}

export const validateUser = async (token: string): Promise<string> => {
  const data = {
    token
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users/validate', 'POST', data)
  } catch (e) {
    throw new Error('There was a problem confirming your account.')
  }
  if (response.status === 200) {
    const json = await response.json()
    return json.token
  } else if (response.status === 412) {
    throw new Error('You\'ve already confirmed your account.\n\nLog in with the email address and password you used to create this account.\n\nIf you\'ve forgotten your password, try going to the Log In screen and clicking "Forgot Password."')
  } else {
    throw new Error('There was an error confirming your account. Please try again.\n\nIf this error continues, try going to the Log In screen and clicking "Forgot Password."\n\nFollowing the steps to reset your password will confirm your account.')
  }
}

export const validatePasswordToken = async (token: string): Promise<[boolean, boolean | string]> => {
  const data = {
    token
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users/passwordTokenValidate', 'POST', data)
  } catch (e) {
    throw new Error ('There was a problem validating your validation token.')
  }
  if (response.status === 200) {
    return [true, true]
  } else {
    return [false, (await response.json()).message]
  }
}

export const resetPassword = async (password: string, token: string): Promise<[boolean, string]> => {
  const data = {
    password,
    token
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users/resetPassword', 'POST', data)
  } catch (e) {
    throw new Error('There was an error resetting your password.')
  }
  if (response.status === 200) {
    return [true, (await response.json()).token]
  } else {
    return [false, 'There was a problem resetting your password. Please request a new password reset link and try again.']
  }
}

export const getUserValidationDataFromEmail = async (emailAddress: string): Promise<any> => {
  const data = {
    emailAddress,
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users/getUserValidationData', 'POST', data)
  } catch (e) {
    throw new Error(e as string)
  }

  return response.json()
}

/* Returns 200 if valid email address. Returns 400 if email address is already in use. */
export const validateEmailAddressWithServer = async (emailAddress: string): Promise<boolean> => {
  const data = {
    emailAddress,
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users/email', 'POST', data)
  } catch (e) {
    throw new Error(e as string)
  }
  if (response.status === 200) {
    return true
  }
  if (response.status === 400) {
    return false
  }
  throw new Error('Failure validating Email Address')
}

export const setNotificationPreference = async (notificationToken: string, notificationType: string, enabled: boolean): Promise<void> => {
  const data = {
    notificationToken,
    notificationType,
    enabled,
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users/setNotificationPreference', 'POST', data)
  } catch (e) {
    throw new Error(e as string)
  }

  if (response.status !== 200) {
    throw new Error()
  }
}

export const postValidateUserFromVerificationCode = async ({ code, emailAddress }: { code: string, emailAddress: string }): Promise<string> => {
  const data = {
    code,
    emailAddress,
  }

  let response: Response
  try {
    response = await makeAPIRequest('/users/validateUserFromVerificationCode', 'POST', data)
  } catch (e) {
    throw new Error('There was a problem confirming your account.')
  }
  if (response.status === 200) {
    const json = await response.json()
    return json.jwtToken
  } else if (response.status === 412) {
    throw new Error('You\'ve already confirmed your account.')
  } else if (response.status === 400) {
    throw new Error((await response.json()).message)
  } else {
    throw new Error('There was an error verifying your account. Please try again.\n\nIf this error continues, try going to the Log In screen and tapping "Forgot Password".\n\nFollowing the steps to reset your password will confirm your account.')
  }
}

export const verifyInviteCodeWithServer = async (inviteCode: string): Promise<boolean> => {
  const data = {
    code: inviteCode,
  }
  let response: Response
  try {
    response = await makeAPIRequest('/classrooms/code', 'POST', data)
  } catch (e) {
    throw new Error(e as string)
  }
  if (response.status === 200) {
    return true
  }
  if (response.status === 400) {
    return false
  }
  throw new Error('Failure validating API code')
}

export const postResendVerificationCode = async (emailAddress: string): Promise<void> => {
  const data = {
    emailAddress,
  }

  try {
    await makeAPIRequest('/users/resendVerificationCode', 'POST', data)
  } catch (e) {
    throw new Error()
  }
}

export const getNotificationPreferences = async (notificationToken: string): Promise<any> => {
  const data = {
    notificationToken
  }
  let response: Response
  try {
    response = await makeAPIRequest('/users/getNotificationPreferences', 'POST', data)
  } catch (e) {
    throw new Error(e as string)
  }

  if (response.status !== 200) {
    throw new Error()
  }

  return response.json()
}

export const postSSOLogin = async ({ token, ssoType }: { token: string, ssoType: string }): Promise<string> => {
  const data = {
    token,
    ssoType,
    longSession: true,
  }
  let response: Response
  try {
    response = await makeAPIRequest('/login/sso', 'POST', data)
  } catch (e) {
    throw new LoginError('error', 'There was an error logging in. Please try again.')
  }
  if (response.status === 200) {
    const json = await response.json()
    return json.token
  }
  if (response.status === 401) {
    const json = await response.json()
    throw new LoginError(json.code, null, json.message)
  } else {
    throw new LoginError('error', null, 'There was an error logging in. Please try again.')
  }
}
